import React, { useMemo, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar, APP } from "../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../components/desktopUi";
import { appImages } from "../assets/appicons";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { EMP_BASE_PATH, EMP_API_BASE_URL } from "../config";

const fmt = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");
const genderLabel = (g) =>
  g === "MALE" || g === 1 ? "Nam" : g === "FEMALE" || g === 2 ? "Nữ" : g ? "Khác" : "-";

const InfoRow = ({ label, value, last }) => (
  <Flex justify="space-between" align="flex-start" gap="16px" py="12px" borderBottom={last ? "none" : "0.5px solid #EEF1F6"}>
    <Text fontSize="13px" color="#51607B" flexShrink={0}>
      {label}
    </Text>
    <Text fontSize="13px" fontWeight={600} color={APP.textPrimary} textAlign="right" wordBreak="break-word">
      {value || value === 0 ? value : "-"}
    </Text>
  </Flex>
);

const Section = ({ title, rows }) => (
  <Box mt="12px" bg={APP.surface} borderRadius="14px" border="1px solid" borderColor={APP.cardBorder} px="16px" py="6px">
    <Text fontSize="14px" fontWeight={600} color={APP.textPrimary} py="10px">
      {title}
    </Text>
    {rows.map((r, i) => (
      <InfoRow key={r.label} label={r.label} value={r.value} last={i === rows.length - 1} />
    ))}
  </Box>
);

const Badge = ({ children, gradient }) => (
  <Box display="inline-block" bgImage={gradient} color="white" fontSize="11px" fontWeight={600} px="6px" py="4px" borderRadius="4px">
    {children}
  </Box>
);

const TABS = [
  { key: "working", label: "Công việc" },
  { key: "contact", label: "Liên hệ" },
  { key: "document", label: "Giấy tờ" },
  { key: "account", label: "Tài khoản" },
];

const AccountSettings = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { user } = useEmployeeAuth();
  const [tab, setTab] = useState("working");

  const avatarUri = useMemo(() => {
    const raw = user?.avatar;
    if (!raw) return appImages.empty_avatar;
    if (raw.startsWith("http")) return raw;
    const base = EMP_API_BASE_URL.replace(/\/+$/, "");
    const path = raw.startsWith("/") ? raw : `/${raw}`;
    return `${base}${path}`;
  }, [user?.avatar]);

  const workingRows = [
    { label: "Chi nhánh", value: user?.branch?.name },
    { label: "Khối", value: user?.saleBlock?.name },
    { label: "Vùng", value: user?.saleArea?.name },
    { label: "Ngày bắt đầu làm việc", value: user?.startTime ? fmt(user.startTime) : "-" },
  ];
  const contactRows = [
    { label: "Số điện thoại", value: user?.phone },
    { label: "Email", value: user?.email },
    { label: "Địa chỉ", value: user?.currentAddress },
    { label: "Ngày sinh", value: user?.dateOfBirth ? fmt(user.dateOfBirth) : "-" },
    { label: "Giới tính", value: genderLabel(user?.gender) },
  ];
  const documentRows = [
    { label: "Mã số thuế", value: user?.taxCode },
    { label: "CCCD/CMND", value: user?.identificationNumber },
    { label: "Ngày cấp", value: user?.dateOfIdentification ? fmt(user.dateOfIdentification) : "-" },
    { label: "Nơi cấp", value: user?.placeOfIdentification },
    { label: "Địa chỉ thường trú", value: user?.permanentAddress },
  ];
  const paymentRows = [
    { label: "Số tài khoản", value: user?.bankAccount },
    { label: "Tên ngân hàng", value: user?.bankName },
  ];
  const passwordRows = [{ label: "Mật khẩu", value: "••••••••" }];
  const deviceRows = [
    { label: "Mã thiết bị", value: "-" },
    { label: "Hệ điều hành", value: (typeof navigator !== "undefined" && navigator.platform) || "Web" },
    { label: "Phiên bản ứng dụng", value: "Web" },
  ];

  const pageBody = (
    <>

      {/* Tab header */}
      <Flex bg={APP.surface} borderBottom="1px solid" borderColor={APP.cardBorder} position="sticky" top="52px" zIndex={10}>
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <Flex
              key={t.key}
              as="button"
              type="button"
              flex="1"
              direction="column"
              align="center"
              onClick={() => setTab(t.key)}
              pt="12px"
            >
              <Text fontSize="13px" fontWeight={600} color={active ? APP.primary200 : "#848484"}>
                {t.label}
              </Text>
              <Box mt="8px" h="2px" w="100%" bg={active ? APP.primary200 : "transparent"} />
            </Flex>
          );
        })}
      </Flex>

      <Box px="16px" pt="8px">
        {/* Avatar chỉ hiện ở tab Công việc (khớp app) */}
        {tab === "working" && (
          <Flex mt="8px" align="center" gap="16px">
            <Box
              w="96px"
              h="96px"
              borderRadius="full"
              bg="#DCE7F7"
              flexShrink={0}
              backgroundImage={`url(${avatarUri})`}
              backgroundSize="cover"
              backgroundPosition="center"
            />
            <Box flex="1" minW="0">
              <Text fontSize="15px" fontWeight={600} color={APP.textPrimary} noOfLines={2}>
                {user?.fullName || "Nhân viên"}
              </Text>
              <Text fontSize="13px" color="#51607B" mt="6px">
                {user?.code || ""}
              </Text>
              <Flex mt="8px" gap="8px" flexWrap="wrap">
                {user?.jobPosition?.name && <Badge gradient={APP.centerGradient}>{user.jobPosition.name}</Badge>}
                {user?.businessCategory?.name && (
                  <Badge gradient="linear-gradient(90deg, #F0B921 0%, #EB1E47 100%)">{user.businessCategory.name}</Badge>
                )}
              </Flex>
            </Box>
          </Flex>
        )}

        {tab === "working" && <Section title="Thông tin công việc" rows={workingRows} />}
        {tab === "contact" && <Section title="Thông tin liên hệ" rows={contactRows} />}
        {tab === "document" && <Section title="Thông tin giấy tờ" rows={documentRows} />}
        {tab === "account" && (
          <>
            <Section title="Thông tin thanh toán" rows={paymentRows} />
            <Section title="Mật khẩu" rows={passwordRows} />
            <Section title="Thiết bị đăng nhập" rows={deviceRows} />
          </>
        )}
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Thông tin cá nhân" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg={APP.appBg} minH="100%" pb="90px">
      <AppBar title="Thông tin cá nhân" onBack={() => navigate(`${EMP_BASE_PATH}/tai-khoan`)} />
      {pageBody}
    </Box>
  );
};

export default AccountSettings;
